/**
 * shadcn Alert (Tailwind 3 "new-york"), ported per the checklist in
 * agents/CODING_STANDARDS.md -> "Styling: two systems": an inline message
 * with an icon slot, a title and a description. The consumer passes the
 * lucide icon as the first child; the layout pins it top-left and indents
 * the text beside it (the v3 absolute-icon layout, since the current grid
 * layout needs `:has()`, which the Chromium 91 target drops).
 *
 * Variants `destructive`, `success`, `warning` and `info` tint the
 * background and colour the border and the icon on the palette tokens; the
 * text stays `foreground` for contrast, as MUI's standard alerts darkened
 * theirs.
 */
import React from 'react'
import { cva } from 'class-variance-authority'

import { cn } from '../../utils/cn.js'

const alertVariants = cva(
  'tw-relative tw-w-full tw-rounded-control tw-border tw-px-4 tw-py-3 tw-text-sm tw-text-foreground [&>svg+div]:tw-translate-y-[-3px] [&>svg]:tw-absolute [&>svg]:tw-left-4 [&>svg]:tw-top-4 [&>svg]:tw-h-4 [&>svg]:tw-w-4 [&>svg~*]:tw-pl-7',
  {
    variants: {
      variant: {
        default: 'tw-bg-background tw-border-border [&>svg]:tw-text-foreground',
        destructive:
          'tw-border-destructive/50 tw-bg-destructive/10 [&>svg]:tw-text-destructive',
        success:
          'tw-border-success/50 tw-bg-success/10 [&>svg]:tw-text-success',
        warning:
          'tw-border-warning/50 tw-bg-warning/10 [&>svg]:tw-text-warning',
        info: 'tw-border-info/50 tw-bg-info/10 [&>svg]:tw-text-info',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const Alert = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    data-slot="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <h5
      ref={ref}
      data-slot="alert-title"
      className={cn(
        'tw-mb-1 tw-font-medium tw-leading-none tw-tracking-tight',
        className,
      )}
      {...props}
    >
      {children}
    </h5>
  ),
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="alert-description"
    className={cn('tw-text-sm [&_p]:tw-leading-relaxed', className)}
    {...props}
  />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription, alertVariants }
